import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { BiBuildings, BiUser, BiCreditCard, BiGroup, BiCog } from 'react-icons/bi'
import { authService } from '../services/authService'
import { TbLogout2 } from 'react-icons/tb'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { RiSettings3Line } from 'react-icons/ri'
import { FaRegUser } from 'react-icons/fa'
import logoText from '../assets/logo-text.png'
import bgDark from '../assets/bg-dark.jpg'
import { IconType } from 'react-icons'
import { useCurrentUser } from '../hooks/useCurrentUser'

interface LayoutProps {
  children: React.ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const { user, isLoading } = useCurrentUser()
  const [isSystemAdmin, setIsSystemAdmin] = useState(false)

  // Update system admin status when user changes
  useEffect(() => {
    setIsSystemAdmin(user?.roles?.includes('admin') || false)
  }, [user])

  // Define navigation items based on user role
  const navigationItems: Array<{ path: string; name: string; icon: IconType }> = [
    { path: '/agencies', name: 'Agencies', icon: BiBuildings },
    ...(isSystemAdmin
      ? [
        { path: '/users', name: 'Users', icon: BiGroup },
        { path: '/settings', name: 'Settings', icon: BiCog },
      ]
      : []),
    { path: '/account', name: 'Account', icon: BiUser },
  ]

  // Function to check if the current path matches the navigation item
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return user?.email?.split('@')[0] || 'User'
  }

  // Get user initials
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    }
    const name = getUserDisplayName()
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  // Handle logout
  const handleLogout = async () => {
    try {
      const response = await authService.logout();
      if (response.success) {
        navigate('/auth/login');
      } else {
        console.error('Logout failed:', response.error);
      }
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/auth/login');
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 relative">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${bgDark})`,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backgroundBlendMode: 'darken',
          backgroundSize: '20%',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat'
        }}
      />
      <div className="absolute inset-0 bg-blue-900/40" />

      {/* Content */}
      <div className="relative z-10">
        {/* Sidebar */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-800 shadow-lg transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            {/* BA Avatar Style Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-lg select-none shadow">
                BA
              </div>
              <span className="text-xl font-semibold text-slate-900 dark:text-white tracking-tight">Billing Away</span>
            </div>
          </div>

          <nav className="mt-6">
            <div className="px-6 mb-4">
              <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Navigation
              </h2>
            </div>

            <div className="space-y-1 px-3">
              {navigationItems.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive(item.path)
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-r-2 border-primary-600'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </button>
                )
              })}
            </div>
          </nav>

          {/* User Profile Section */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                {getUserInitials()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">
                  {getUserDisplayName()}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {user?.email}
                </p>
              </div>
              <div className="relative">
                <button
                  className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                  onClick={handleLogout}
                >
                  <TbLogout2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
          {/* Header */}
          <header className="bg-white dark:bg-slate-800 shadow-sm border-b border-slate-200 dark:border-slate-700">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {getUserInitials()}
                    </div>
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-200">
                      {getUserDisplayName()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-6">
            {children}
          </main>
        </div>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  )
}

export default Layout 